import { useEffect, useState } from 'react';
import { RequiredFund } from '@/common/types';
import { getMissingFunds } from '@/common/util';

interface UseMissingFundsParams {
  requiredFund: RequiredFund;
  walletAddress?: string;
}

interface UseMissingFundsResult {
  missingFunds: RequiredFund | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useMissingFunds({
  requiredFund,
  walletAddress,
}: UseMissingFundsParams): UseMissingFundsResult {
  const [missingFunds, setMissingFunds] = useState<RequiredFund | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMissingFunds = async () => {
    if (!walletAddress) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const funds = await getMissingFunds(walletAddress, requiredFund);
      setMissingFunds(funds);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch missing funds'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMissingFunds();
  }, [walletAddress, requiredFund.address, requiredFund.amount.toString()]);

  return {
    missingFunds,
    isLoading,
    error,
    refetch: fetchMissingFunds,
  };
}
